/**
 * Análises › Estado na URL (D528; research doc 05 §4 point 11): a screen's question as a link - versioned, compressed, and NEVER carrying a
 * personal value.
 *
 *  - The link holds `?s=v1.<token>`. The token is the state as JSON, compressed (LZW over the UTF-8 bytes, 12-bit codes) and written in
 *    base64url, so a long list of values stays a link a person can paste. The version is part of the token: a link made by an older or a
 *    newer build is refused with a reason, never half-read.
 *  - {@link toUrlState} REMOVES every condition that pins a customer, a driver, a courier or an order (the same rule as the server's
 *    `stripPersonalValues`, which the server applies again when the link is opened) and says which dimensions lost one, so the screen can show
 *    a notice. A link carries the question, never a person.
 *  - Opening a link decodes and validates it HERE only to restore the screen; whatever it names is validated AGAIN by the server
 *    (`POST /v1/analytics/filters/check`: the planner's own parser, the caller's own reach) before a single figure is asked for - the link
 *    grants nothing.
 *
 * Pure: no I/O, no DOM (`readToken` / `shareUrl` take the pieces of a location).
 */
import { stripPersonalValues } from '@vello/analytics-semantic';
import { BI_PANEL_IDS, type BiPanelId } from './bi-panels.js';
import { type BiCompareChoice, type BiPeriod, BI_ALL_PRESETS, BI_COMPARE_CHOICES, lastDaysValid } from './bi-core.js';
import { type ExplorerState, parseExplorerState } from './bi-explorer.js';
import { type BiFilterSpec, type BiFilterState } from './bi-filters.js';
import { type CompareAgainst } from '@vello/analytics-semantic';

export const URL_STATE_VERSION = 1;
export const URL_STATE_PARAM = 's';
/** A link longer than this is not offered (browsers and chat tools cut long URLs): the screen says so instead of sharing a broken one. */
export const URL_STATE_MAX_TOKEN = 6000;

// ───────────────────────────── compression: LZW, 12-bit codes, base64url ─────────────────────────────

const MAX_CODE = 4095;

/**
 * The words every state is made of. The dictionary starts with them (and every prefix of them), so a short state - which is all a link ever
 * holds - compresses from its first byte instead of having to learn its own field names. Changing this list changes every token: it is part of
 * the version (`URL_STATE_VERSION`).
 */
const PRESET: readonly string[] = [
  '{"kind":"explorer","explorer":{"mode":"table","metrics":["',
  '{"kind":"panels","panel":"',
  '"dimensions":["',
  '"grain":"total"',
  '"filters":[{"dimension":"',
  '"op":"in","values":["',
  '"op":"not_in","values":["',
  '"op":"between","values":["',
  '"op":"contains","values":["',
  '"op":"is_null","values":[]',
  '"anyOf":[[{"dimension":"',
  '"having":[{"metric":"',
  '"period":{"kind":"preset","preset":"',
  '"compare":"auto","upToNowAgainst":"last_year_by_date"',
  '"sortDir":"desc","pageSize":25,"page":0}}',
  '"bar":{"filters":[',
  '"currency":"',
  '"kind":"lastDays","days":',
  '"kind":"custom","from":"20',
  'last_30_days', 'last_7_days', 'month_to_date', 'year_to_date', 'this_week', 'last_week', 'this_quarter', 'last_quarter',
  'vertical', 'merchant', 'market', 'country', 'weekday', 'channel', 'status', 'outcome', 'cancel_reason', 'payment_method',
  'order_distance', 'order_duration', 'orders', 'gmv', 'commission', 'average_ticket', 'cancellation_rate', 'completion_rate',
  'previous_period', 'same_period_last_year_by_weekday', 'same_period_last_year_by_date', 'up_to_now',
];

function presetEntries(): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const word of PRESET) {
    for (let n = 2; n <= word.length; n += 1) {
      const prefix = word.slice(0, n);
      if (!seen.has(prefix)) {
        seen.add(prefix);
        out.push(prefix);
      }
    }
  }
  return out;
}

function lzwEncode(bytes: Uint8Array): Uint8Array {
  const dict = new Map<string, number>();
  for (let i = 0; i < 256; i += 1) dict.set(String.fromCharCode(i), i);
  let next = 256;
  for (const entry of presetEntries()) dict.set(entry, next++);
  const codes: number[] = [];
  let w = '';
  for (const b of bytes) {
    const c = String.fromCharCode(b);
    const wc = w + c;
    if (dict.has(wc)) {
      w = wc;
    } else {
      codes.push(dict.get(w) as number);
      if (next <= MAX_CODE) dict.set(wc, next++);
      w = c;
    }
  }
  if (w !== '') codes.push(dict.get(w) as number);
  // two 12-bit codes in three bytes; an odd last code takes the next two (its trailing 4 bits are zero and the decoder ignores them)
  const out: number[] = [];
  for (let i = 0; i < codes.length; i += 2) {
    const a = codes[i] as number;
    const b = codes[i + 1];
    out.push(a >> 4, ((a & 0xf) << 4) | (b === undefined ? 0 : b >> 8));
    if (b !== undefined) out.push(b & 0xff);
  }
  return Uint8Array.from(out);
}

/** The most a token may expand to. A state is a few KB of JSON; a token that decodes to more than this is not one (a crafted run of one byte expands without bound). */
const MAX_DECODED_BYTES = 64 * 1024;

function lzwDecode(data: Uint8Array): Uint8Array | undefined {
  const codes: number[] = [];
  for (let i = 0; i < data.length; ) {
    const hi = data[i] as number;
    const mid = data[i + 1];
    if (mid === undefined) return undefined;
    codes.push((hi << 4) | (mid >> 4));
    const lo = data[i + 2];
    if (lo === undefined) {
      // an odd last code: its second half is the zero padding
      if ((mid & 0xf) !== 0) return undefined;
      i += 2;
    } else {
      codes.push(((mid & 0xf) << 8) | lo);
      i += 3;
    }
  }
  if (codes.length === 0) return new Uint8Array();
  // The dictionary is a tree: every entry is (the entry it extends, one byte), so building one is O(1) and an entry is never copied; the
  // output is written once, backwards from the end of each entry, into a buffer that cannot outgrow MAX_DECODED_BYTES.
  const parent: number[] = [];
  const last: number[] = [];
  const size: number[] = [];
  const head: number[] = [];
  for (let i = 0; i < 256; i += 1) {
    parent.push(-1);
    last.push(i);
    size.push(1);
    head.push(i);
  }
  let next = 256;
  const preset = new Map<string, number>();
  for (const entry of presetEntries()) {
    const base = entry.length === 2 ? entry.charCodeAt(0) : (preset.get(entry.slice(0, -1)) as number);
    parent.push(base);
    last.push(entry.charCodeAt(entry.length - 1));
    size.push((size[base] as number) + 1);
    head.push(head[base] as number);
    preset.set(entry, next);
    next += 1;
  }
  const out = new Uint8Array(MAX_DECODED_BYTES);
  let at = 0;
  const emit = (code: number): boolean => {
    const length = size[code] as number;
    if (at + length > MAX_DECODED_BYTES) return false;
    let position = at + length - 1;
    for (let c = code; c >= 0; c = parent[c] as number) out[position--] = last[c] as number;
    at += length;
    return true;
  };
  const firstCode = codes[0] as number;
  if (firstCode >= next || !emit(firstCode)) return undefined;
  let prev = firstCode;
  for (let i = 1; i < codes.length; i += 1) {
    const code = codes[i] as number;
    if (code > next) return undefined;
    const first = code < next ? (head[code] as number) : (head[prev] as number);
    if (next <= MAX_CODE) {
      parent.push(prev);
      last.push(first);
      size.push((size[prev] as number) + 1);
      head.push(head[prev] as number);
      next += 1;
    }
    if (code >= next || !emit(code)) return undefined;
    prev = code;
  }
  return out.slice(0, at);
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] as number;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64[a >> 2];
    out += B64[((a & 3) << 4) | ((b ?? 0) >> 4)];
    if (b !== undefined) out += B64[((b & 15) << 2) | ((c ?? 0) >> 6)];
    if (c !== undefined) out += B64[c & 63];
  }
  return out;
}

function fromBase64Url(text: string): Uint8Array | undefined {
  if (!/^[A-Za-z0-9_-]*$/.test(text) || text.length % 4 === 1) return undefined;
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 4) {
    const v = [0, 1, 2, 3].map((k) => (i + k < text.length ? B64.indexOf(text[i + k] as string) : -1));
    out.push(((v[0] as number) << 2) | ((v[1] as number) >> 4));
    if ((v[2] as number) >= 0) out.push((((v[1] as number) & 15) << 4) | ((v[2] as number) >> 2));
    if ((v[3] as number) >= 0) out.push((((v[2] as number) & 3) << 6) | (v[3] as number));
  }
  return Uint8Array.from(out);
}

const utf8 = new TextEncoder();
const unutf8 = new TextDecoder('utf-8', { fatal: true });

/** Text to a compact URL-safe token, and back (undefined when the token is not one). */
export function packText(text: string): string {
  return toBase64Url(lzwEncode(utf8.encode(text)));
}

export function unpackText(token: string): string | undefined {
  const raw = fromBase64Url(token);
  if (raw === undefined) return undefined;
  const bytes = lzwDecode(raw);
  if (bytes === undefined) return undefined;
  try {
    return unutf8.decode(bytes);
  } catch {
    return undefined;
  }
}

// ───────────────────────────── the state ─────────────────────────────

export interface PanelsUrlState {
  readonly kind: 'panels';
  readonly panel: BiPanelId;
  readonly period: BiPeriod;
  readonly compare: BiCompareChoice;
  readonly upToNowAgainst: CompareAgainst;
  /** The global filter bar (no HAVING: a bar has none). */
  readonly bar: BiFilterState;
}

export interface ExplorerUrlState {
  readonly kind: 'explorer';
  readonly explorer: ExplorerState;
}

export type UrlState = PanelsUrlState | ExplorerUrlState;

export interface UrlStateResult {
  /** The link's token (`v1.…`), or undefined when it would be too long to share. */
  readonly token: string | undefined;
  /** The dimensions whose personal conditions were left out. */
  readonly stripped: readonly string[];
  /** The state as the link holds it (without the personal values). */
  readonly state: UrlState;
}

function stripBuilder<T extends { filters: readonly BiFilterSpec[]; anyOf: readonly (readonly BiFilterSpec[])[] }>(value: T): { value: T; stripped: readonly string[] } {
  const { state, stripped } = stripPersonalValues({ filters: value.filters, anyOf: value.anyOf });
  const s = state as { filters?: BiFilterSpec[]; anyOf?: BiFilterSpec[][] };
  return { value: { ...value, filters: s.filters ?? [], anyOf: s.anyOf ?? [] }, stripped };
}

/** The state a link may carry (personal values removed) and its token. */
export function toUrlState(state: UrlState): UrlStateResult {
  let clean: UrlState;
  let stripped: readonly string[];
  if (state.kind === 'panels') {
    const s = stripBuilder(state.bar);
    clean = { ...state, bar: { ...s.value, having: [] } };
    stripped = s.stripped;
  } else {
    const s = stripBuilder(state.explorer);
    // The page a person was on is not the question; a link opens the first page.
    clean = { kind: 'explorer', explorer: { ...s.value, page: 0 } };
    stripped = s.stripped;
  }
  const token = `v${URL_STATE_VERSION}.${packText(JSON.stringify(clean))}`;
  return { token: token.length <= URL_STATE_MAX_TOKEN ? token : undefined, stripped, state: clean };
}

export type UrlStateError = 'EMPTY' | 'VERSION' | 'FORMAT' | 'CONTENT';

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function parseBuilder(raw: unknown): BiFilterState | undefined {
  if (!isRecord(raw)) return undefined;
  // the explorer parser already knows how to read conditions strictly: borrow it through a minimal state
  const probe = parseExplorerState({
    mode: 'table', metrics: [], dimensions: [], grain: 'total', filters: raw.filters ?? [], anyOf: raw.anyOf ?? [], having: raw.having ?? [],
    period: { kind: 'preset', preset: 'today' }, compare: 'none', upToNowAgainst: 'last_year_by_date', sortDir: 'desc', pageSize: 25, page: 0,
  });
  return probe === undefined ? undefined : { filters: probe.filters, anyOf: probe.anyOf, having: probe.having };
}

/** Read a token. Everything it holds is validated by shape; the SERVER validates it again before anything is asked. */
export function decodeUrlState(token: string | null | undefined): { readonly ok: true; readonly state: UrlState } | { readonly ok: false; readonly error: UrlStateError } {
  if (token === null || token === undefined || token === '') return { ok: false, error: 'EMPTY' };
  const m = /^v(\d{1,3})\.(.*)$/.exec(token);
  if (m === null) return { ok: false, error: 'FORMAT' };
  if (Number(m[1]) !== URL_STATE_VERSION) return { ok: false, error: 'VERSION' };
  if (token.length > URL_STATE_MAX_TOKEN) return { ok: false, error: 'FORMAT' };
  const text = unpackText(m[2] as string);
  if (text === undefined) return { ok: false, error: 'FORMAT' };
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: 'FORMAT' };
  }
  if (!isRecord(data)) return { ok: false, error: 'CONTENT' };
  if (data.kind === 'explorer') {
    const explorer = parseExplorerState(data.explorer);
    return explorer === undefined ? { ok: false, error: 'CONTENT' } : { ok: true, state: { kind: 'explorer', explorer } };
  }
  if (data.kind === 'panels') {
    const bar = parseBuilder(data.bar);
    const p = data.period;
    let period: BiPeriod | undefined;
    if (isRecord(p) && p.kind === 'preset' && typeof p.preset === 'string' && (BI_ALL_PRESETS as readonly string[]).includes(p.preset)) period = { kind: 'preset', preset: p.preset as (typeof BI_ALL_PRESETS)[number] };
    else if (isRecord(p) && p.kind === 'lastDays' && typeof p.days === 'number' && lastDaysValid(p.days)) period = { kind: 'lastDays', days: p.days };
    else if (isRecord(p) && p.kind === 'custom' && typeof p.from === 'string' && typeof p.to === 'string') period = { kind: 'custom', from: p.from, to: p.to };
    const against = data.upToNowAgainst;
    if (
      bar === undefined || period === undefined || typeof data.panel !== 'string' || !(BI_PANEL_IDS as readonly string[]).includes(data.panel) ||
      typeof data.compare !== 'string' || !(BI_COMPARE_CHOICES as readonly string[]).includes(data.compare) ||
      (against !== 'previous_period' && against !== 'last_year_by_date' && against !== 'last_year_by_weekday') || bar.having.length > 0
    ) {
      return { ok: false, error: 'CONTENT' };
    }
    return { ok: true, state: { kind: 'panels', panel: data.panel as BiPanelId, period, compare: data.compare as BiCompareChoice, upToNowAgainst: against, bar } };
  }
  return { ok: false, error: 'CONTENT' };
}

// ───────────────────────────── the location ─────────────────────────────

/** The token in a `?…` string, if any. */
export function readToken(search: string): string | undefined {
  const value = new URLSearchParams(search).get(URL_STATE_PARAM);
  return value === null || value === '' ? undefined : value;
}

/** `search` with the token set (or removed when `token` is undefined), other parameters kept. */
export function withToken(search: string, token: string | undefined): string {
  const params = new URLSearchParams(search);
  if (token === undefined) params.delete(URL_STATE_PARAM);
  else params.set(URL_STATE_PARAM, token);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/** The absolute link to share: the page's own address with the token. */
export function shareUrl(location: { readonly origin: string; readonly pathname: string; readonly search: string }, token: string): string {
  return `${location.origin}${location.pathname}${withToken(location.search, token)}`;
}
